import { type CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../../router/paths';
import { getProjects, type ProjectTagId } from '../../data/projects';

type ProjectsShowcaseProps = {
  badge?: string;
  title: string;
  description: string;
  limit?: number;
  showAllButton?: boolean;
};

const projectTagMeta: Record<
  ProjectTagId,
  {
    icon: string;
    className: string;
  }
> = {
  react: {
    icon: 'ri-reactjs-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(56,189,248,0.3)_0%,rgba(14,165,233,0.16)_100%)] text-white/86',
  },
  crm: {
    icon: 'ri-database-2-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(124,58,237,0.3)_0%,rgba(91,33,182,0.16)_100%)] text-white/86',
  },
  codeIgniter: {
    icon: 'ri-code-box-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(249,115,22,0.3)_0%,rgba(194,65,12,0.16)_100%)] text-white/86',
  },
  frontend: {
    icon: 'ri-layout-4-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(56,189,248,0.28)_0%,rgba(2,132,199,0.16)_100%)] text-white/86',
  },
  backend: {
    icon: 'ri-server-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(163,230,53,0.28)_0%,rgba(77,124,15,0.16)_100%)] text-white/88',
  },
  wordpress: {
    icon: 'ri-wordpress-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(96,165,250,0.28)_0%,rgba(37,99,235,0.16)_100%)] text-white/86',
  },
  uxui: {
    icon: 'ri-pencil-ruler-2-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(232,160,181,0.28)_0%,rgba(190,24,93,0.16)_100%)] text-white/86',
  },
  redesign: {
    icon: 'ri-magic-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(251,191,36,0.3)_0%,rgba(217,119,6,0.16)_100%)] text-white/86',
  },
  reservations: {
    icon: 'ri-calendar-schedule-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(52,211,153,0.28)_0%,rgba(5,150,105,0.16)_100%)] text-white/86',
  },
  presentationWeb: {
    icon: 'ri-window-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(196,181,253,0.28)_0%,rgba(139,92,246,0.15)_100%)] text-white/86',
  },
  leadForm: {
    icon: 'ri-file-list-3-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(163,230,53,0.3)_0%,rgba(101,163,13,0.16)_100%)] text-white/88',
  },
  recruitment: {
    icon: 'ri-team-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(253,164,175,0.28)_0%,rgba(225,29,72,0.16)_100%)] text-white/86',
  },
  menu: {
    icon: 'ri-restaurant-2-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(251,146,60,0.3)_0%,rgba(194,65,12,0.16)_100%)] text-white/86',
  },
  orders: {
    icon: 'ri-shopping-bag-3-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(248,113,113,0.3)_0%,rgba(185,28,28,0.16)_100%)] text-white/86',
  },
  gastro: {
    icon: 'ri-restaurant-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(249,115,22,0.3)_0%,rgba(194,65,12,0.16)_100%)] text-white/86',
  },
  events: {
    icon: 'ri-ticket-2-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(45,212,191,0.28)_0%,rgba(13,148,136,0.16)_100%)] text-white/86',
  },
  campaigns: {
    icon: 'ri-megaphone-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(251,146,60,0.28)_0%,rgba(234,88,12,0.16)_100%)] text-white/86',
  },
  portfolio: {
    icon: 'ri-layout-grid-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.06)_100%)] text-white/82',
  },
  personalBrand: {
    icon: 'ri-user-star-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(244,114,182,0.28)_0%,rgba(192,38,211,0.15)_100%)] text-white/86',
  },
  services: {
    icon: 'ri-briefcase-4-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(163,230,53,0.3)_0%,rgba(77,124,15,0.16)_100%)] text-white/88',
  },
  functionalities: {
    icon: 'ri-settings-3-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(251,191,36,0.3)_0%,rgba(217,119,6,0.16)_100%)] text-white/86',
  },
  construction: {
    icon: 'ri-building-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(89,214,207,0.28)_0%,rgba(13,148,136,0.16)_100%)] text-white/86',
  },
  health: {
    icon: 'ri-heart-pulse-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(90,95,179,0.3)_0%,rgba(67,56,202,0.16)_100%)] text-white/86',
  },
  multilingual: {
    icon: 'ri-translate-2',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(47,105,255,0.28)_0%,rgba(29,78,216,0.16)_100%)] text-white/86',
  },
  blog: {
    icon: 'ri-article-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(239,68,68,0.28)_0%,rgba(185,28,28,0.16)_100%)] text-white/86',
  },
  affiliate: {
    icon: 'ri-links-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(168,85,247,0.28)_0%,rgba(126,34,206,0.16)_100%)] text-white/86',
  },
  wellness: {
    icon: 'ri-leaf-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(201,138,87,0.28)_0%,rgba(146,64,14,0.16)_100%)] text-white/86',
  },
  customProduct: {
    icon: 'ri-tools-line',
    className:
      'border-white/10 bg-[linear-gradient(180deg,rgba(148,163,184,0.28)_0%,rgba(71,85,105,0.16)_100%)] text-white/86',
  },
};

function getProjectTagMeta(tag: ProjectTagId) {
  return (
    projectTagMeta[tag] ?? {
      icon: 'ri-price-tag-3-line',
      className:
        'border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.1)_0%,rgba(255,255,255,0.04)_100%)] text-white/78',
    }
  );
}

export default function ProjectsShowcase({
  badge = 'Projekty',
  title,
  description,
  limit,
  showAllButton = false,
}: ProjectsShowcaseProps) {
  const { t } = useTranslation();
  const allProjects = useMemo(() => getProjects(t), [t]);
  const visibleProjects = typeof limit === 'number' ? allProjects.slice(0, limit) : allProjects;

  return (
    <section className="relative overflow-hidden bg-[#0D0D0D] py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-10%] top-14 h-80 w-80 rounded-full bg-brand-green/10 blur-3xl" />
        <div className="absolute right-[-8%] top-24 h-[28rem] w-[28rem] rounded-full bg-brand-ocean/10 blur-3xl" />
        <div className="absolute bottom-[-18%] left-[30%] h-72 w-72 rounded-full bg-white/5 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="mb-12 max-w-3xl">
          <span className="mb-5 inline-flex items-center rounded-full border border-white/10 bg-white/[0.045] px-4 py-2 text-sm font-semibold text-brand-green shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md">
            <span className="mr-2 inline-flex items-center">
              <i className="ri-layout-grid-line text-base" />
            </span>
            {badge}
          </span>

          <h2 className="mb-6 text-5xl font-caprasimo text-white md:text-6xl">{title}</h2>
          <p className="max-w-2xl text-lg leading-relaxed text-white/72 md:text-xl">
            {description}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {visibleProjects.map((project, index) => (
            <a
              key={project.title}
              href={project.href}
              target={project.external ? '_blank' : undefined}
              rel={project.external ? 'noreferrer' : undefined}
              className="group relative block min-h-[26rem] overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.03] shadow-[0_16px_34px_rgba(0,0,0,0.14)] transition-all duration-500 hover:-translate-y-1 hover:border-white/12 hover:shadow-[0_22px_44px_rgba(0,0,0,0.2)] md:min-h-[29rem] xl:min-h-[31rem]"
              style={
                {
                  '--project-arrow-hover': project.arrowHoverColor ?? '#A3E635',
                } as CSSProperties
              }
            >
              <img
                src={project.image}
                alt={project.title}
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={index < 2 ? 'high' : 'low'}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                style={{ objectPosition: project.imagePosition ?? 'center top' }}
              />
              <div className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0)_50%,rgba(0,0,0,0.28)_72%,rgba(0,0,0,0.88)_100%)]" />

              <div className="absolute left-5 top-5 z-10">
                <span className="inline-flex rounded-full border border-white/10 bg-black/30 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-white/75 backdrop-blur-md">
                  {project.category}
                </span>
              </div>

              <span className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-black/30 text-white/72 backdrop-blur-md transition-all duration-300 group-hover:scale-[1.06] group-hover:border-white/12 group-hover:shadow-[0_14px_30px_rgba(0,0,0,0.18)]">
                <span
                  className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{ backgroundColor: 'var(--project-arrow-hover)' }}
                />
                <i className="ri-arrow-right-up-line relative z-10 text-[1.05rem] font-semibold text-white/88 transition-all duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:scale-[1.18] group-hover:text-white" />
              </span>

              <div className="absolute inset-x-0 bottom-0 z-10">
                <div className="rounded-t-[20px] bg-black/28 p-4 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition-all duration-500 ease-out group-hover:translate-y-2 group-hover:opacity-10 md:group-hover:opacity-0 sm:p-4.5">
                  <h3 className="w-full text-[1.35rem] font-caprasimo leading-[1.04] text-white sm:text-[1.65rem]">
                    {project.title}
                  </h3>

                  <p className="mt-2 w-full text-[0.86rem] leading-5 text-white/68 sm:text-[0.9rem]">
                    {project.summary}
                  </p>

                  {project.tags.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {project.tags.slice(0, 3).map((tag) => {
                        const meta = getProjectTagMeta(tag);

                        return (
                        <span
                          key={tag}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.68rem] font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-md ${meta.className}`}
                        >
                          <i className={`${meta.icon} text-[0.78rem]`} />
                          {t(`projectsData.tags.${tag}`)}
                        </span>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </a>
          ))}
        </div>

        {showAllButton ? (
          <div className="mt-10 flex justify-center">
            <a
              href={PATHS.projekty}
              className="group inline-flex items-center gap-3 rounded-full border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.03)_100%)] px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(0,0,0,0.16)] backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-green/28 hover:text-brand-green"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-green text-[#0D0D0D] shadow-[0_10px_22px_rgba(163,230,53,0.2)]">
                <i className="ri-stack-line text-sm" />
              </span>
              <span>{t('home.projects.more')}</span>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.06] text-white/76 transition-colors duration-300 group-hover:text-brand-green">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </a>
          </div>
        ) : null}
      </div>
    </section>
  );
}
